import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type {
  CreateContentItemInput,
  ListContentItemsInput,
  QuestionImageInput,
} from '../../domain/entities/content.entity';

export function useContentItems(input: ListContentItemsInput) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: [
      'content',
      'items',
      input.page,
      input.perPage,
      input.type ?? '',
      input.status ?? '',
      input.includeDeleted ?? false,
      input.questionFormat ?? '',
      input.annulled ?? '',
      input.examId ?? '',
    ],
    queryFn: () => repository.listContentItems(input),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

export function useContentItem(contentId: string | undefined) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'item', contentId],
    queryFn: () => repository.getContentItem(contentId!),
    enabled: !!contentId,
    retry: false,
  });
}

function useInvalidateItems() {
  const queryClient = useQueryClient();
  return (contentId?: string) => {
    void queryClient.invalidateQueries({ queryKey: ['content', 'items'] });
    // Toda escrita de content item pode mudar o preview de uma prova (o item é
    // a própria prova, ou uma questão dentro dela) — invalidar o prefixo é mais
    // barato que descobrir a qual prova a questão pertence.
    void queryClient.invalidateQueries({
      queryKey: ['content', 'exam-preview'],
    });
    if (contentId) {
      void queryClient.invalidateQueries({
        queryKey: ['content', 'item', contentId],
      });
    }
  };
}

export function useCreateContentItem() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (input: CreateContentItemInput) =>
      repository.createContentItem(input),
    onSuccess: () => invalidate(),
  });
}

/** `images` só faz sentido quando o item é `QUESTION` (imagem nova no texto). */
export function useUpdateContentItemBody(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (input: { body: unknown; images?: QuestionImageInput[] }) =>
      repository.updateContentItemBody(contentId, input.body, input.images),
    onSuccess: (item) => invalidate(item.id),
  });
}

export function usePublishContentItem() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (contentId: string) => repository.publishContentItem(contentId),
    onSuccess: (item) => invalidate(item.id),
  });
}

export function useArchiveContentItem() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (contentId: string) => repository.archiveContentItem(contentId),
    onSuccess: (item) => invalidate(item.id),
  });
}

export function useDeleteContentItem() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (contentId: string) => repository.deleteContentItem(contentId),
    onSuccess: (item) => invalidate(item.id),
  });
}

/**
 * Exclui UMA questão pelo mesmo caminho do preview da prova
 * (`DELETE questions/bulk`), e não pelo delete genérico: é esse caminho que
 * tira a questão da prova dona e refaz a numeração — o genérico só marca o item
 * e deixa a posição vaga. Rascunho é apagado de vez; publicada vira tombstone.
 * O resultado diz qual dos dois foi (`mode`).
 */
export function useDeleteQuestion() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: async (questionId: string) => {
      const [result] = await repository.bulkDeleteQuestions([questionId]);
      return result!;
    },
    onSuccess: (result) => invalidate(result.questionId),
  });
}
